import { getHeaders } from 'auth/auth';
import { api } from 'constants/constant';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

const resolveTenantApiBase = () => api || '';

export const underwritingAPI = createApi({
  reducerPath: 'underwriting',
  tagTypes: ['underwritingResults', 'pipelineReport', 'archivedReports'],
  baseQuery: fetchBaseQuery({
    baseUrl: resolveTenantApiBase(),
    credentials: 'include',
  }),
  endpoints: (builder) => ({
    // Get underwriting results for an application
    getUnderwritingResults: builder.query({
      query: ({ applicationId }) => {
        const queryParams = applicationId ? `?application_id=${applicationId}` : '';
        return {
          url: `b2b/ai/underwriting${queryParams}`,
          method: 'GET',
          headers: getHeaders(),
        };
      },
      // This prevents RTK Query from treating 404 as an error, since we want to handle "no results" gracefully
      validateStatus: (response, result) => {
        return response.status === 200 || response.status === 404;
      },
      // Transform the response to handle both successful and 404 cases
      transformResponse: (response, meta) => {
        // If the response is an error message (404), return an empty array
        if (meta?.response?.status === 404) {
          return [];
        }
        if (!response) {
          return [];
        }
        if (Array.isArray(response)) {
          return response;
        }
        const summaryPayload = response.summary || {};
        const analysisResult =
          response.analysis_result ||
          {
            ...summaryPayload,
            remarks: response.remarks || summaryPayload.remarks || [],
          };
        const normalized = {
          ...response,
          analysis_result: analysisResult,
          created_at: response.created_at || response.generated_at,
          file_names: response.file_names || [],
        };
        return [normalized];
      },
      keepUnusedDataFor: 3600, // Keep data for 1 hour
      providesTags: (result, error, { applicationId, userId }) => [
        { type: 'underwritingResults', id: `${applicationId}-${userId || 'none'}` }
      ],
    }),

    // Run the deterministic pipeline and store temp report in MongoDB
    runPipeline: builder.mutation({
      query: ({ applicationId, runToken }) => ({
        url: `b2b/loans/pre-underwriting/run-pipeline/${applicationId}`,
        method: 'POST',
        // The screen's id for this run. Stamped onto every progress blob the
        // run writes, so the poller below can tell them from the last run's.
        params: runToken ? { run_token: runToken } : {},
        headers: getHeaders(),
      }),
      invalidatesTags: (result, error, { applicationId }) => [
        { type: 'pipelineReport', id: applicationId },
      ],
    }),

    // The deterministic half of the report — verdict, metrics, levers, blockers
    // and per-category counts. Finishes in seconds and renders on its own, so it
    // is deliberately NOT bundled with the AI result that takes minutes.
    getPipelineReport: builder.query({
      query: ({ applicationId }) => ({
        url: `b2b/loans/pre-underwriting/pipeline-report/${applicationId}`,
        method: 'GET',
        headers: getHeaders(),
      }),
      // Matches the agent's half above. Moving between tabs on a borrower
      // unmounts this screen constantly, and on the 60-second default the
      // report was dropped while its sibling was not — so returning meant a
      // fetch in flight and the page rendering complete with the checks
      // missing. Age costs nothing here: the query revalidates on every mount,
      // and re-running the analysis invalidates the tag outright.
      keepUnusedDataFor: 3600,
      // A file that has never been run 404s. That is an empty state, not an error.
      validateStatus: (response) =>
        response.status === 200 || response.status === 404,
      transformResponse: (response, meta) =>
        meta?.response?.status === 404 ? null : response?.data ?? null,
      providesTags: (result, error, { applicationId }) => [
        { type: 'pipelineReport', id: applicationId },
      ],
    }),

    /* How far through a run the agent is. Polled only while one is in flight.

       The agent's half takes minutes and had one signal for all of it — the
       finished report — so the progress bar sat on the same step for the whole
       wait. This is the one real event in between: the income stage finishing.
       Never cached, because a stale answer here is worse than no answer.

       `runToken` is part of the query args deliberately, not just the URL: RTK
       keys its cache on the args, so a new run cannot read the previous run's
       entry even for the instant before the first request returns. That instant
       is what drew the last run's finished pills at the top of the next one. */
    getRunProgress: builder.query({
      query: ({ applicationId, runToken }) => ({
        url: `b2b/loans/pre-underwriting/run-progress/${applicationId}`,
        method: 'GET',
        // The server drops any progress blob belonging to a different run, so
        // this screen never has to reason about whose progress it is holding.
        params: runToken ? { run_token: runToken } : {},
        headers: getHeaders(),
      }),
      transformResponse: (response) => response?.data ?? null,
      keepUnusedDataFor: 0,
    }),

    // File the finished report in the documents store. Idempotent on the
    // agent's report version, so a re-fire returns the document already on
    // file rather than creating a second copy.
    archiveReport: builder.mutation({
      query: ({ applicationId }) => ({
        url: `b2b/loans/pre-underwriting/archive-report/${applicationId}`,
        method: 'POST',
        headers: getHeaders(),
      }),
      invalidatesTags: (result, error, { applicationId }) => [
        { type: 'archivedReports', id: applicationId },
      ],
    }),

    // Past reports, newest first. Each row carries the document id the
    // existing documents download route takes.
    getArchivedReports: builder.query({
      query: ({ applicationId }) => ({
        url: `b2b/loans/pre-underwriting/reports/${applicationId}`,
        method: 'GET',
        headers: getHeaders(),
      }),
      validateStatus: (response) =>
        response.status === 200 || response.status === 404,
      transformResponse: (response, meta) =>
        meta?.response?.status === 404 ? [] : response?.data ?? [],
      providesTags: (result, error, { applicationId }) => [
        { type: 'archivedReports', id: applicationId },
      ],
    }),

    // This is now only for manual/direct analysis, not called after /underwrite
    analyzeApplication: builder.mutation({
      query: ({ data, userId, applicationId }) => {
        return {
          url: `ai/underwriting/analyze?user_id=${userId}&application_id=${applicationId}`,
          method: 'POST',
          headers: getHeaders(),
          body: data,
        };
      },
      invalidatesTags: (result, error, { userId, applicationId }) => [
        { type: 'underwritingResults', id: `${applicationId}-${userId}` }
      ],
    }),
  }),
});

export const {
  useGetUnderwritingResultsQuery,
  useLazyGetUnderwritingResultsQuery,
  useRunPipelineMutation,
  useGetPipelineReportQuery,
  useGetRunProgressQuery,
  useArchiveReportMutation,
  useGetArchivedReportsQuery,
  useLazyGetPipelineReportQuery,
  useAnalyzeApplicationMutation,
} = underwritingAPI;
